/**
 * navigation.js — the ONE nav source of truth (RW-1784), re-cut per the
 * RW-1782 navigation IA (RW-1785).
 *
 * Structure only: plain data, no JSX, no hooks, no flag evaluation. The
 * renderers own everything dynamic — Sidebar.js keeps rendering, sub-rail
 * state, flag filtering (isFeatureEnabled) and role gating (roleAllows);
 * DealsNav.js keeps its mobile-only pill rendering. Any additional nav
 * surface (⌘K, breadcrumbs, CI route gates) reads THIS tree rather than
 * growing a copy — two hand-maintained trees drifted once already
 * (C_NAV, retired by RW-1784).
 *
 * RW-1785 bands: a grouped section holds EITHER `items: []` (flat) OR
 * `bands: [{ label, items: [] }]`. Bands are PRESENTATIONAL ONLY — no
 * route, not clickable, not collapsible, never a third nav tier. A null
 * band label means "no heading" (the section's unlabeled remainder).
 * Renderers that don't draw band labels flatten via sectionItems().
 *
 * Item shape (RW-1599: `icon` is a plain string name resolved by the
 * renderer's NavIcon map — never an imported component, per CLAUDE.md
 * gotcha #11):
 *   { key, label, path, flagKey?, badgeKey?, icon?, noActive?,
 *     adminOnly? | trainerOrAdmin? | staffOnly? | staffSelfOnly? |
 *     reviewOnly? | leadOnly? }
 * Section shape:
 *   { id, icon, label, subtitle?, railKey?, items[] | bands[] } or
 *   { id, icon, label, standalone: true, path, flagKey?, railKey?, badgeKey? }
 *
 * RW-2581 `railKey` — a rail-CURATION flag, deliberately NOT `flagKey`:
 *   flagKey  asks "does this module exist for you". Admins bypass it
 *            (isFeatureEnabled short-circuits on isAdmin).
 *   railKey  asks "is this section on the rail". Admins bypass it too
 *            (RW-2624 — Admin view shows every section). RW-2581 shipped it
 *            with no bypass so the admin could see the curated rail, but that
 *            hid four sections from every admin. The admin/agent view toggle
 *            already does that job: under "Viewing as Agent" `isAdmin` is
 *            false, so the admin sees exactly the rail the agents get.
 * A railKey hides the section from the rail and from the mobile bottom bar.
 * It does NOT close the routes underneath and it does NOT leave nav search
 * (navDestinations) — /deals, /training and /sign stay live pages, reachable
 * by URL, deep link and search, gated only by their own module flags. Shut a
 * module with its flagKey; take a section off the rail with its railKey.
 */

export const SECTIONS = [
  {
    id: 'home', icon: 'home', label: 'Home',
    standalone: true, path: '/dashboard', flagKey: 'dashboard',
    mobileTab: 1, // RW-2378

  },
  {
    // RW-1785 (RW-1782 decision 2): Coming Soon / Buyer Needs are CONNECT
    // destinations — agent-to-agent market discovery is the daily-open
    // habit, so they file under a Market band here rather than by record
    // type under Deals. Their routes stay /deals/* (the One Pipeline
    // surfaces RW-1524 re-implemented); only the filing moved.
    id: 'connect', icon: 'message', label: 'Connect', subtitle: 'Your people',
    bands: [
      {
        label: 'Social',
        items: [
          { key: 'Feed', label: 'Feed', path: '/feed', flagKey: 'feed', icon: 'message', mobileTab: 3 }, // RW-2378
          // RW-1788: a real filter route at last — deep-linkable, shareable,
          // and able to show an active state (the app's query-param
          // convention, like /messages?open=). Renders indented under Feed:
          // it is a view of the feed, not a peer destination.
          { key: 'ImportantPosts', label: 'Important Posts', path: '/feed?filter=important', indent: true, badgeKey: 'unreadImportant', icon: 'megaphone' },
          { key: 'Messages', label: 'Messages', path: '/messages', flagKey: 'messages', badgeKey: 'unreadMessages', icon: 'mail' },
          { key: 'Groups', label: 'Groups', path: '/groups', flagKey: 'groups', icon: 'users' },
          { key: 'Events', label: 'Events', path: '/events', flagKey: 'events', icon: 'calendar' },
          { key: 'Recognition', label: 'Recognition', path: '/recognition', icon: 'award' },
          // Not placed by the RW-1782 design tree — kept in Connect › Social
          // (community surfaces), appended after the designed order.
          { key: 'TownHalls', label: 'Town Halls', path: '/townhalls', flagKey: 'townHalls', icon: 'landmark' },
          { key: 'ConnectSearch', label: 'Search', path: '/connect/search', flagKey: 'connectSearch', icon: 'search' },
        ],
      },
      {
        label: 'Market',
        items: [
          // RW-3647 (Clint, 2026-10-21): filed under Connect, so they live
          // under /connect — URL, breadcrumb and rail now agree. The old
          // /deals/* URLs redirect (LegacyDeepLinkRedirect).
          { key: 'ComingSoon', label: 'Coming Soon', path: '/connect/coming-soon', flagKey: 'comingSoon', icon: 'eye' },
          { key: 'BuyerNeeds', label: 'Buyer Needs', path: '/connect/buyer-needs', flagKey: 'buyerNeeds', icon: 'target' },
          { key: 'SavedPosts', label: 'Saved', path: '/saved', flagKey: 'saved', icon: 'bookmark' },
        ],
      },
      {
        /* RW-2582 (Clint, 2026-09-22): Vendors comes BACK to Connect.
           RW-1785 filed it under Company › Resources as "a service
           directory, not a social surface" — but the move an agent makes is
           to ask the network who to call, the same reflex that opens Coming
           Soon and Buyer Needs. Same /vendors route, same `vendors` flag;
           only the filing moved. This supersedes the RW-1785 placement.

           Its own 'Services' band (Clint's call), deliberately NOT the
           Market band: MARKET_MOBILE_PILLS is derived from Market (minus
           Saved), so a row filed there also becomes a pill on the Coming
           Soon / Buyer Needs mobile strip — a round trip designed as a PAIR,
           with a Rule 26 floor at exactly two. Vendors belongs in Connect,
           not on those boards. navigation.test.js pins that derivation. */
        label: 'Services',
        items: [
          { key: 'Vendors', label: 'Vendors', path: '/vendors', flagKey: 'vendors', icon: 'handshake' },
        ],
      },
    ],
  },
  {
    // RW-1785: renamed from "My Work" — three rows, one meaning: today's
    // work. Pipeline went to Deals (RW-1524 already retired the row),
    // Training to Academy, Leaderboard to Company, FLOW to the spine
    // (RW-1600). "My Deals" (a second door to /deals) retired here; the
    // Deals section owns that destination.
    id: 'myday', icon: 'clipboard', label: 'My Day', subtitle: 'Your day',
    // RW-2581: Focus Mode carries no flagKey, so no combination of module
    // flags could ever empty this section — the rail needed its own gate.
    railKey: 'railMyDay',
    bands: [
      {
        // RW-3670: "Today" groups the rail's rows but is not a place, and in
        // the header trail it read like a date ("My Day › Today › Calendar"
        // over August). The trail says "My Day › Calendar".
        label: 'Today',
        crumb: false,
        items: [
          // RW-1603: "Other Tasks" (not "My Tasks") — deliberately renamed to
          // stop colliding with Deals › My Tasks; the label survives the
          // re-cut. Fold-in end state is tracked on RW-1603.
          { key: 'MyTasks', label: 'Other Tasks', path: '/my-tasks', flagKey: 'myTasks', badgeKey: 'overdue', icon: 'checkSquare' },
          { key: 'Calendar', label: 'Calendar', path: '/calendar', flagKey: 'calendar', icon: 'calendar' },
          { key: 'FocusMode', label: 'Focus Mode', path: '/focus', icon: 'moon' },
        ],
      },
    ],
  },
  {
    // RW-1786: `layers` glyph (DA icon set, what C_NAV used) — `key` read as
    // hardware next to Home's house silhouette on the icon spine.
    id: 'deals', icon: 'layers', label: 'Deals', subtitle: 'Move deals forward',
    // RW-2581: emptying this section by module flag meant turning off
    // listings/buyers/onePipeline/contacts/bba — which closes their ROUTES
    // and bounces the agents still working in them. The rail gate is free.
    // (Contacts has since left this section for its own rail entry, RW-2588.)
    railKey: 'railDeals',
    // RW-2588: no mobileTab. Slot 4 went to Contacts — the bar is four tabs by
    // design, and Deals had lost it anyway the moment railDeals went off. When
    // Deals is on the rail its rows are one tap away in the More drawer.
    bands: [
      {
        // RW-1785: the design's "Pipeline Dashboard" row is deliberately NOT
        // shipped — the surviving report surface (/deals/leadership) is
        // leadOnly, so an agent-facing row would be a dead affordance.
        // RW-1788 owns the final naming once an agent-facing report exists.
        // RW-1787: Leadership and Broker Review live on the STAFF tree only
        // (their role gates are strict subsets of the staff variant, so no
        // agent-tree user could ever see them here).
        label: 'Pipeline',
        items: [
          { key: 'DealsHome', label: 'Home', path: '/deals/home', flagKey: 'onePipeline', icon: 'home' },
          { key: 'OnePipeline', label: 'All Deals', path: '/deals', flagKey: 'onePipeline', icon: 'grid' },
          // RW-2867: the one task inbox (deal + onboarding + other tasks); its
          // badge counts all three kinds (App.js tasksDueCount).
          { key: 'DealsTasks', label: 'My Tasks', path: '/deals/tasks', flagKey: 'onePipeline', badgeKey: 'tasksDue', icon: 'checkSquare' },
        ],
      },
      {
        label: 'By side',
        items: [
          { key: 'Listings', label: 'Listings', path: '/listings', flagKey: 'listings', icon: 'mapPin' },
          { key: 'Buyers', label: 'Buyers', path: '/buyers', flagKey: 'buyers', icon: 'user' },
          // RW-1610: listing presentations / brochures built from listing data.
          { key: 'Flipbooks', label: 'Flipbooks', path: '/flipbooks', flagKey: 'flipbooks', icon: 'presentation' },
          { key: 'BuyerAgreements', label: 'Buyer Agreements', path: '/buyer-agreements', flagKey: 'bba', icon: 'penLine' },
        ],
      },
      {
        // Agent-facing money (da-money: shared by agent + TC + broker) — an
        // earlier IA draft made these staff-only, which took an agent's own
        // GCI off their rail. They stay agent-visible under Deals.
        label: 'Money',
        items: [
          { key: 'DealsCommissions', label: 'Commissions', path: '/deals/commissions', flagKey: 'onePipeline', icon: 'dollar' },
          { key: 'DealsPayouts', label: 'Payouts', path: '/deals/payouts', flagKey: 'onePipeline', icon: 'landmark' },
        ],
      },
      {
        label: 'People',
        items: [
          // RW-2588: Contacts left this band for its own rail entry (below).
          { key: 'DealsPeople', label: 'People', path: '/deals/people', flagKey: 'onePipeline', icon: 'users' },
          { key: 'DealsRoster', label: 'Roster', path: '/deals/roster', flagKey: 'onePipeline', icon: 'briefcase' },
        ],
      },
      {
        // Unlabeled remainder: module utilities. (The review queue moved to
        // the staff tree's Queues section — RW-1787.)
        label: null,
        items: [
          { key: 'DealsDocs', label: 'Documents', path: '/deals/docs', flagKey: 'onePipeline', icon: 'fileText' },
          { key: 'DealsSettings', label: 'Deal Settings', path: '/deals/settings', flagKey: 'onePipeline', icon: 'settings' },
        ],
      },
    ],
  },
  {
    // RW-2588 (Clint, 2026-09-22): Contacts promoted to the MAIN rail. It was
    // a row in Deals › People, and RW-2581 took Deals off the rail — which
    // left the CRM's root record (RW-839) with no rail door at all. Directly
    // before FLOW: FLOW works the book (Ninja lists live on contacts).
    //
    // No badgeKey, deliberately. The old row's badge was the live BOOK SIZE;
    // on a standalone entry that renders as a permanent coral "99+" for
    // nearly everyone, and a badge that never clears teaches people to ignore
    // every badge. The Contacts page shows its own count.
    id: 'contacts', icon: 'book', label: 'Contacts',
    standalone: true, path: '/contacts', flagKey: 'contacts',
    mobileTab: 4, // RW-2588: the slot Deals held (RW-2378)
  },
  {
    // RW-1600 (Clint): FLOW + Academy promoted to the MAIN rail — FLOW is
    // the daily habit (streak badge lives where it's seen before any click).
    // Ask Rêve moved to the global header (assistant, not destination).
    id: 'flow', icon: 'zap', label: 'FLOW',
    standalone: true, path: '/ninja-flow', flagKey: 'ninjaFlow', badgeKey: 'flowStreak',
    mobileTab: 2, // RW-2378

  },
  {
    // RW-2017: My Numbers sits directly after FLOW because it is the same
    // kind of thing — a habit surface an agent opens to see where they
    // stand, not a record to work. Gated on myNumbers (OFF until RW-2013).
    id: 'my-numbers', icon: 'dollar', label: 'My Numbers',
    standalone: true, path: '/my-numbers', flagKey: 'myNumbers',
  },
  {
    // RW-1785: Academy grows from a standalone entry into a section — it
    // absorbs Training (relocated, NOT replaced: same /training route and
    // requiredTraining badge) and Tutorials from the old My Work. The
    // module root keeps its own row so /academy stays reachable. My
    // Onboarding injects into a "Me" band conditionally (Sidebar.js).
    id: 'academy', icon: 'cap', label: 'Academy', subtitle: 'Learn & grow',
    // RW-2581: Tutorials carries no flagKey — same trap as My Day above.
    railKey: 'railAcademy',
    bands: [
      {
        label: null,
        items: [
          { key: 'AcademyHome', label: 'Home', path: '/academy', flagKey: 'academy', icon: 'cap' },
        ],
      },
      {
        label: 'Learn',
        items: [
          { key: 'Training', label: 'Training', path: '/training', flagKey: 'training', badgeKey: 'requiredTraining', icon: 'book' },
          { key: 'Tutorials', label: 'Tutorials', path: '/tutorials', icon: 'compass' },
        ],
      },
    ],
  },
  {
    // RW-494 Rêve Sign. RW-3307: DocuSign retired — one screen, the DocuSeal
    // requests sent for signature. Gated by the reveSign flag.
    id: 'sign', icon: 'pen', label: 'Rêve Sign', subtitle: 'Signature requests',
    // RW-2581: rail curation only. `reveSign` still owns the module itself.
    railKey: 'railSign',
    items: [
      { key: 'SignInbox', label: 'Sent for signature', path: '/sign', flagKey: 'reveSign', icon: 'mail' },
    ],
  },
  {
    id: 'company', icon: 'building', label: 'Company', subtitle: 'The brokerage',
    bands: [
      {
        label: 'The brokerage',
        items: [
          // RW-1785 (RW-1782 decision, do not re-ask): renamed from "People" —
          // colleagues at Rêve, vs Contacts (clients and third parties —
          // its own rail entry since RW-2588). Same /people route.
          { key: 'People', label: 'Agents & Staff', path: '/people', flagKey: 'people', icon: 'users' },
          // RW-1785: from My Work — it ranks the brokerage, not my day.
          { key: 'Leaderboard', label: 'Leaderboard', path: '/leaderboard', flagKey: 'leaderboard', icon: 'trophy' },
          { key: 'Offices', label: 'Offices', path: '/offices', flagKey: 'offices', icon: 'mapPin' },
          { key: 'OrgChart', label: 'Org Chart', path: '/org-chart', flagKey: 'orgChart', icon: 'grid' },
        ],
      },
      {
        label: 'Resources',
        items: [
          { key: 'Documents', label: 'Documents', path: '/documents', flagKey: 'documents', icon: 'fileText' },
          // RW-2493: restored. The row was dropped as collateral in d0d4b2f3
          // (RW-189, an infrastructure commit) in April 2026 and never came
          // back, so RW-1784 had nothing to carry into this file — the module
          // sat reachable-by-URL-only for five months while the page kept
          // taking polish tickets. Company › Resources beside Documents is
          // where the old Sidebar.js row lived (flagKey 'photos', unchanged).
          { key: 'Photos', label: 'Photos', path: '/photos', flagKey: 'photos', icon: 'camera' },
          // RW-2917: Email Signatures lives with the brokerage's resources (it
          // was a one-item Tools section, RW-2895). Its Admin is a staff-only
          // TAB inside the module, never a rail row.
          { key: 'EmailSignatures', label: 'Email Signatures', path: '/email-signatures', flagKey: 'emailSignatures', icon: 'mail' },
          { key: 'TechStack', label: 'Tech Stack', path: '/tech-stack', icon: 'sliders' },
          { key: 'ReferAgent', label: 'Refer an Agent', path: '/refer-agent', icon: 'heart' },
          // RW-1642: the auto-generated changelog. Badge = entries since the
          // user's last visit (badges.whatsNewUnread, computed by changelogApi).
          { key: 'WhatsNew', label: "What's New", path: '/whats-new', flagKey: 'whatsNew', badgeKey: 'whatsNewUnread', icon: 'sparkle' },
        ],
      },
    ],
  },
];

/* Footer: Settings always visible; admin items gated by role. Collapsed into
   a single "More" section in the slim rail. Reports (production reporting)
   and Help (RW-655) arrive from the old My Work — neither is "today's
   work", and RW-655 placed Help beside Settings. */
export const FOOTER_ITEMS = [
  // RW-3740: `keywords` — what Search also finds this page by. Settings is
  // one long page, and "notifications" (the likeliest search there is) found
  // nothing; these are its section headings, plus the words people use.
  { key: 'Settings', icon: 'settings', label: 'Settings', path: '/settings',
    keywords: ['Notifications', 'Email Notifications', 'Push Notifications', 'My Signature', 'Profile', 'Privacy',
      'Preferences', 'Start page', 'Appearance', 'Theme', 'Goals', 'Google Contacts', 'Ninja Selling', 'Focus Mode', 'Sign out'] },
  { key: 'Reports', icon: 'fileText', label: 'Reports', path: '/reports' },
  { key: 'Help', icon: 'help', label: 'Help', path: '/help' },
  { key: 'AdoptionDashboard', icon: 'trending', label: 'Adoption', path: '/admin/adoption', trainerOrAdmin: true },
  { key: 'OnboardingPipeline', icon: 'clipboard', label: 'Onboarding', path: '/onboarding', staffOnly: true },
  { key: 'OffboardingPipeline', icon: 'logout', label: 'Offboarding', path: '/admin/offboarding', adminOnly: true },
  { key: 'AgentRoster', icon: 'users', label: 'Agent Roster', path: '/admin/roster', adminOnly: true },
  { key: 'StaffHR', icon: 'briefcase', label: 'Staff · HR', path: '/staff', adminOnly: true },
  // Phase 5 (RW-1068): staff_self users land on My Space at /staff (App.js gate
  // renders the restricted self-service surface there) — this is discoverability.
  { key: 'MySpace', icon: 'user', label: 'My Space', path: '/staff', staffSelfOnly: true },
  { key: 'Recruiting', icon: 'target', label: 'Recruiting', path: '/admin/recruiting', adminOnly: true },
  { key: 'Admin', icon: 'wrench', label: 'Admin', path: '/admin', adminOnly: true },
];

/* ════════════════════════════════════════════════════════════════════════
   RW-1787 — the STAFF tree: a SUPERSET of the agent tree, never a subset.
   Staff (admin / tc / broker — the DealsRolesContext review set; resolution
   happens in Sidebar.js, no new role system) reach every feature an agent
   can, under the same section, band, and item names — the shared pieces
   below are the same OBJECT REFERENCES as the agent tree, so vocabulary
   identity holds by construction. What staff add:
     - Queues (second — clearing a queue is why staff open the app) and
       Roster sections, both role-gated within;
     - a Company › Admin band;
     - Leadership in Deals › Pipeline (leadOnly).
   Rows whose gates are strict subsets of the staff variant (Broker Review:
   reviewOnly = admin|tc|broker; Leadership: leadOnly = admin|broker) left
   the agent tree entirely — no agent-tree user could ever see them there.
   The design's TC Queue, Compliance Review, Offboarding-as-designed,
   Feature Flags and Templates rows are NOT shipped: no routes exist yet
   (/compliance-review is a redirect to /deals/review, the ONE queue —
   RW-1783 finding). No dead affordances.
   ════════════════════════════════════════════════════════════════════════ */
const DEALS_REVIEW = { key: 'DealsReview', label: 'Broker Review', path: '/deals/review', flagKey: 'onePipeline', reviewOnly: true, icon: 'shield' };
const DEALS_LEADERSHIP = { key: 'DealsLeadership', label: 'Leadership', path: '/deals/leadership', flagKey: 'onePipeline', leadOnly: true, icon: 'trending' };

const agentSection = id => SECTIONS.find(s => s.id === id);
const footRow = key => FOOTER_ITEMS.find(it => it.key === key);

export const STAFF_SECTIONS = [
  agentSection('home'),
  {
    id: 'queues', icon: 'shield', label: 'Queues', subtitle: 'Clear the files',
    bands: [
      { label: 'Files', items: [DEALS_REVIEW] },
    ],
  },
  {
    ...agentSection('deals'),
    bands: agentSection('deals').bands.map(b =>
      b.label === 'Pipeline' ? { ...b, items: [...b.items, DEALS_LEADERSHIP] } : b),
  },
  {
    // Tammy's daily surfaces out from behind the footer gear. The rows are
    // the FOOTER_ITEMS objects themselves — same labels, paths, and gates.
    id: 'roster', icon: 'users', label: 'Roster', subtitle: 'The lifecycle',
    bands: [
      { label: 'Lifecycle', items: [footRow('OnboardingPipeline'), footRow('Recruiting'), footRow('OffboardingPipeline')] },
      { label: null, items: [footRow('AgentRoster'), footRow('StaffHR')] },
    ],
  },
  {
    // RW-2017: Brokerage — the staff reporting home (RW-2014). Reports is
    // PROMOTED here out of the staff footer rather than duplicated: staff
    // open production reporting daily, and it was sitting behind the gear.
    // It is the same FOOTER_ITEMS row (same key, label, path, gates), so
    // there is still exactly one definition of it.
    id: 'brokerage', icon: 'trending', label: 'Brokerage', subtitle: 'How the firm is doing',
    bands: [
      { label: 'Reporting', items: [footRow('Reports')] },
    ],
  },
  agentSection('connect'),
  agentSection('myday'),
  // RW-2588: same object as the agent tree, and the same spot — before FLOW.
  agentSection('contacts'),
  agentSection('flow'),
  // RW-2017: staff keep My Numbers too. The staff shell REORGANIZES the agent
  // tree, it never drops reachability (RW-1787 superset invariant) — and a
  // producing broker has their own book to look at, distinct from Brokerage.
  agentSection('my-numbers'),
  agentSection('academy'),
  agentSection('sign'),
  {
    ...agentSection('company'),
    bands: [
      ...agentSection('company').bands,
      { label: 'Admin', items: [footRow('Admin'), footRow('AdoptionDashboard')] },
    ],
  },
];

/* Staff footer: the admin rows moved into Queues/Roster/Company › Admin;
   what remains is the personal remainder. MySpace stays for the edge case
   of a staff-variant user who also carries staff_self.
   RW-2017: Reports left this list when it was promoted into the Brokerage
   group above — one row, one place in the staff shell. */
export const STAFF_FOOTER_ITEMS = ['Settings', 'Help', 'MySpace'].map(footRow);

/* ── Flatten a section's rows regardless of band structure. Renderers that
   do not draw band labels (today's rail, the mobile drawer) consume this;
   the two-tier chrome (RW-1786) reads `bands` directly. ── */
export function sectionItems(section) {
  if (section.standalone) return [];
  if (section.items) return section.items;
  return section.bands.flatMap(b => b.items);
}

/* ── Lookup: every item (section + footer) by key. Views below and any
   external consumer resolve through this so a re-filed item (a key moving
   between sections) never breaks them — keys are the stable handle, paths
   and grouping are not. ── */
export function navItemByKey(key) {
  for (const s of [...SECTIONS, ...STAFF_SECTIONS]) {
    if (s.standalone) continue;
    const hit = sectionItems(s).find(it => it.key === key);
    if (hit) return hit;
  }
  return FOOTER_ITEMS.find(it => it.key === key) || null;
}

/* ── Which rail section owns the current route. Extracted from Sidebar.js by
   RW-2329 so it sits with the other pure path→nav helpers and can be tested
   directly instead of through a mirror.

   `pathActive` is unchanged: exact, or a path prefix on a segment boundary.
   The prefix half is load-bearing — /connect/coming-soon/{id} has no nav row
   of its own and reaches Connect only by prefixing /connect/coming-soon
   (RW-3647 moved both boards from /deals/*). ── */
export function pathActive(path, current) {
  return current === path || current.startsWith(path + '/');
}

/* 🔴 RW-2329 — THE reported bug. This used to be one pass returning the first
   section holding any pathActive row, so the answer depended on the ORDER of
   the list it was handed. The two lists are ordered differently:

     SECTIONS        home > connect > myday > deals > …
     STAFF_SECTIONS  home > queues > deals > roster > brokerage > connect > …

   Deals' OnePipeline row is `/deals`, and pathActive('/deals',
   '/deals/coming-soon') is TRUE. On the agent rail Connect is reached first
   and wins. On the STAFF rail Deals is reached three sections earlier — so a
   staff or admin user clicking Connect › Market › Coming Soon watched the rail
   light up DEALS. That is exactly what Clint reported, and it is invisible to
   any probe that only walks SECTIONS.

   The fix is generic and order-independent: of every row whose path is active
   for this pathname, the LONGEST one wins. Ties keep today's answer (first
   section in list order), so a path listed in two sections is unchanged.

   NOT a reorder of the staff rail — that is a visible product change nobody
   asked for — and NOT a special case for these two routes.

   Longest-match rather than the narrower "exact first, then prefix": an exact
   row is automatically the longest possible match, so this subsumes that rule,
   and it is the only version that also fixes the RECORD routes.
   /deals/coming-soon/{id} has no row of its own, so an exact pass misses it
   entirely and the prefix fallback is still order-dependent — it kept
   answering `deals` on the staff rail. Longest-match picks /deals/coming-soon
   (18 chars) over /deals (6) and lands on Connect, so a board and its records
   finally agree, on both rails. navigation.sectionForPath.test.js pins that.
   RW-3647 then moved both boards to /connect/*, so /deals no longer prefixes
   them at all; longest-match stays as the order-independent rule. */
export function sectionForPath(railSections, pathname) {
  let bestId = null;
  let bestLen = -1;
  for (const s of railSections) {
    const paths = s.standalone
      ? [s.path]
      // sectionItems is THE flatten helper for this file (docblock, top);
      // it returns [] for a standalone section, which the branch above
      // already handled.
      : sectionItems(s).filter(it => !it.noActive).map(it => it.path);
    for (const p of paths) {
      // `>` not `>=`: an equal-length match later in the list must not steal
      // the answer from an earlier section (ties keep list order, as before).
      if (pathActive(p, pathname) && p.length > bestLen) { bestLen = p.length; bestId = s.id; }
    }
  }
  return bestId;
}

/* ── RW-1786: breadcrumb trail for the topbar — Section › Band › Item.
   EXACT path matches only: detail routes (/listings/abc) keep their richer
   App.js titles. noActive rows (Important Posts → /feed) are filters, never
   the crumb. Standalone sections return { section, band: null, item: null }
   — the renderer shows just the section (the design's Home suppression).
   Footer items crumb under the synthetic "More" section. Role-agnostic:
   for the one role-disjoint duplicate path (/staff) it returns the first
   row's label. A band marked `crumb: false` (RW-3670) is left out. ── */
export function navTrailForPath(pathname) {
  for (const s of [...SECTIONS, ...STAFF_SECTIONS]) {
    if (s.standalone) {
      if (s.path === pathname) return { section: s.label, band: null, item: null };
      continue;
    }
    const bands = s.bands || [{ label: null, items: s.items }];
    for (const band of bands) {
      const hit = band.items.find(it => !it.noActive && it.path === pathname);
      if (hit) return { section: s.label, band: band.crumb === false ? null : band.label, item: hit.label };
    }
  }
  const foot = FOOTER_ITEMS.find(it => it.path === pathname);
  if (foot) return { section: 'More', band: null, item: foot.label };
  return null;
}

/* RW-3700: a Connect detail page keeps its rail row's trail — "Connect ›
   Social › Groups" inside a room, where the header used to drop to a bare
   "Groups", and "… › Events" on an event rather than "Event Detail". Only
   the detail routes are mapped; /events/mine and /events/new keep their own
   titles. App.js asks this only after navTrailForPath finds no exact row. */
const CONNECT_DETAIL_PARENTS = [
  [/^\/groups\/[^/]+$/, '/groups'],
  [/^\/events\/(?!mine$|new$)[^/]+$/, '/events'],
  [/^\/recognition\/.+$/, '/recognition'],
];
export function connectDetailTrail(pathname) {
  const hit = CONNECT_DETAIL_PARENTS.find(([re]) => re.test(pathname || ''));
  return hit ? navTrailForPath(hit[1]) : null;
}

/* ── Named view: the Deals mobile pill strip (DealsNav.js, RW-1507).
   Grouping mirrors the retired C_NAV order (Work | Money | Company); the
   label overrides are the strip's deliberate short forms — including the
   rows whose rail labels the RW-1785 re-cut changed (the strip keeps its
   original wording; it is a deal-module surface, not the rail). Derived
   from the tree above so it cannot drift — the navigation.test.js view
   test pins the exact [path, label] output. ── */
const DEALS_PILL_LABELS = {
  OnePipeline: 'Board',
  DealsSettings: 'Settings',
};
const DEALS_PILL_GROUPS = [
  ['DealsHome', 'OnePipeline', 'DealsTasks', 'DealsPeople', 'ComingSoon', 'BuyerNeeds'],
  ['DealsCommissions', 'DealsPayouts', 'DealsReview', 'DealsLeadership'],
  ['DealsRoster', 'DealsDocs', 'DealsSettings'],
];
// RW-2298: each pill also carries its row's flagKey so DealsNav can gate a
// pill exactly as the rail gates the row (isFeatureEnabled). Before this the
// strip was ungated and offered members pills to routes that bounced them.
export const DEALS_MOBILE_GROUPS = DEALS_PILL_GROUPS.map(group =>
  group.map(key => {
    const item = navItemByKey(key);
    if (!item) throw new Error(`DealsNav pill references missing nav item: ${key}`);
    return [item.path, DEALS_PILL_LABELS[key] || item.label, item.flagKey || null];
  }),
);

/* ── Named view: the Connect › Market mobile pill strip (MarketNav.js,
   RW-2329). Coming Soon and Buyer Needs used to render DealsNav as their own
   header, which is what made them read as Deals screens. They get a strip of
   the band they actually live in instead.

   ⚠ The strip is the Market band MINUS Saved — deliberately, on Clint's call.
   The band is right to hold all three: Saved is a Market destination on the
   rail. The STRIP is not, for one concrete reason — /saved (SavedPosts.js)
   renders no MarketNav, so a Saved pill here would be a ONE-WAY exit: tap it
   on mobile and the only way back to the boards is the hamburger drawer.
   Coming Soon and Buyer Needs are a genuine pair (the match engine crosses
   them) and both render this strip, so between those two it is a real round
   trip. Saved stays reachable from the sidebar, unchanged. If SavedPosts ever
   renders this strip, add 'SavedPosts' back below.

   Resolved by KEY through navItemByKey, exactly like DEALS_PILL_GROUPS above,
   and for the reason stated at that function: keys are the stable handle,
   paths and labels are not. The first cut of this list found the band by
   `label === 'Market'` and threw when it missed — which meant a purely
   cosmetic rename of a PRESENTATIONAL band label (this file's own words, up
   top) threw at module load, and App.js imports this file eagerly, so the
   production failure mode of that edit was a white screen at boot.

   Nothing here throws now: an unresolvable key is dropped, and MarketNav's
   Rule 26 floor renders nothing at all below two live destinations — which at
   two pills is load-bearing, not theoretical. The loud failure belongs in CI,
   and it is there: navigation.test.js pins this list against the band with the
   one exclusion named, so a row added to, removed from, re-ordered within or
   re-filed out of the Market band fails the suite. ── */
const MARKET_PILL_KEYS = ['ComingSoon', 'BuyerNeeds'];
export const MARKET_MOBILE_PILLS = MARKET_PILL_KEYS
  .map(navItemByKey)
  .filter(Boolean)
  .map(it => [it.path, it.label, it.flagKey || null]);

/* ── RW-2378: the ONE visibility computation, shared by the desktop rail and
   the mobile bottom bar.

   MobileNav used to build itself from two hardcoded module lists with no
   connection to this tree, so 8 of the 12 top-level sections never appeared
   on a phone's primary nav at all, and it still offered Training — a module
   that no longer exists. That is the paired-registry class: defined in one
   list, reached via another, with nothing asserting they agree (RW-1782,
   RW-2155, RW-2329 are the same shape).

   Extracted verbatim from Sidebar's own `visibleSections` memo so the two
   surfaces CANNOT disagree — not reimplemented beside it, which is how the
   drift started.

   Injected predicates, because this module stays free of React context and
   flag evaluation (see the file header):
     roleAllows(item)    → navRoleAllows(item, gates)
     isFlagEnabled(key)  → isFeatureEnabled(key, flags, isAdmin)
     isPathAllowed(path) → wind-down allowlist, or () => true
     isRailEnabled(key)  → isFeatureEnabled(key, flags, isAdmin)

   RW-2581: isRailEnabled is a SECOND predicate rather than a reuse of
   isFlagEnabled so "is this module on" and "is this section on the rail"
   stay separate questions. RW-2624 gave it the same admin bypass (it had
   none), so today both renderers build the two predicates the same way.
   Callers that do not curate a rail (tests, the manifest generator) may
   omit it; the default admits everything, so a section with no railKey
   behaves exactly as before.                                              ── */
export function visibleNavSections(sections, {
  roleAllows = () => true,
  isFlagEnabled = () => true,
  isPathAllowed = () => true,
  isRailEnabled = () => true,
} = {}) {
  return (sections || []).map((s) => {
    // Rail curation runs FIRST and applies to standalone and grouped alike:
    // "not on the rail" is a statement about the section, not about the rows
    // inside it or the role reading it.
    if (s.railKey && !isRailEnabled(s.railKey)) return null;
    if (s.standalone) {
      if (!isPathAllowed(s.path)) return null;
      return roleAllows(s) && isFlagEnabled(s.flagKey) ? s : null;
    }
    const allow = (it) => roleAllows(it) && isFlagEnabled(it.flagKey) && isPathAllowed(it.path);
    const bands = (s.bands || [{ label: null, items: s.items }])
      .map((b) => ({ ...b, items: (b.items || []).filter(allow) }))
      .filter((b) => b.items.length);
    const items = bands.flatMap((b) => b.items);
    return items.length ? { ...s, items, bands } : null;
  }).filter(Boolean);
}

/* ── RW-2378: the mobile bottom bar, derived from an explicit `mobileTab`
   field rather than a second list. Ordered BY THAT NUMBER, never by position
   in the tree — STAFF_SECTIONS reorders the same sections (it puts Deals
   before Connect), and ordering by position would silently give staff a
   different bottom bar from agents. That exact trap is RW-2329.

   A grouped section resolves to its first VISIBLE row, so a tab never points
   at something the caller's flags or role would have withheld. A section or
   row that is gated out simply has no tab — the bar shrinks rather than
   offering a dead affordance. ── */
export function mobileTabRows(visibleSections) {
  const rows = [];
  const push = (node, path, icon, label, matchPaths) => {
    if (!path) return;
    rows.push({
      key: node.key || node.id,
      label,
      path,
      icon,
      order: node.mobileTab,
      // Every route this tab should light up for. A grouped section's tab
      // stays lit anywhere inside the section, not only on the one row it
      // happens to open — otherwise tapping Deals lands you on a Deals screen
      // with no tab highlighted.
      matchPaths: (matchPaths || [path]).filter(Boolean),
    });
  };
  for (const s of visibleSections || []) {
    if (s.mobileTab) {
      // A standalone section carries its own path; a grouped one borrows its
      // first surviving destination.
      push(
        s,
        s.standalone ? s.path : (s.items || [])[0]?.path,
        s.icon,
        s.label,
        s.standalone ? [s.path] : (s.items || []).map((it) => it.path),
      );
    }
    if (s.standalone) continue;
    for (const item of s.items || []) {
      if (item.mobileTab) push(item, item.path, item.icon || s.icon, item.label);
    }
  }
  // Stable: equal orders keep tree order, and a duplicate number is a
  // navigation.js authoring bug the RW-2378 guard test fails on.
  return rows.sort((a, b) => a.order - b.order);
}

/* ── RW-1568: the role gate, shared by the rail and by search.
   Extracted from Sidebar's inline `roleAllows` so the two cannot disagree —
   a destination search offers must be one the rail would also render, or
   we have built a new dead end while closing an old one (Rule 26).

   `gates` mirrors what App.js already computes for the rail:
     { isAdmin, isTrainer, isStaff, staffSelf, dealRole } ── */
export function navRoleAllows(item, gates = {}) {
  const { isAdmin, isTrainer, isStaff, staffSelf, dealRole } = gates;
  if (item.adminOnly) return !!isAdmin;
  if (item.trainerOrAdmin) return !!(isAdmin || isTrainer);
  if (item.staffOnly) return !!isStaff;
  if (item.staffSelfOnly) return !!staffSelf;
  if (item.reviewOnly) return !!(isAdmin || dealRole === 'tc' || dealRole === 'broker');
  if (item.leadOnly) return !!(isAdmin || dealRole === 'broker');
  return true;
}

/* ── RW-1568: every place a user can actually GO, as flat searchable rows.
   Maya could not find the org chart. The row was in the rail the whole time
   (Company › The brokerage, flag on) — but the move a lost user actually
   makes is to type it into search, and search indexed Firestore content
   only. A live page that search cannot reach is a dead end.

   Derived from the tree above rather than a hand-kept second list: RW-1782
   established that anything duplicating this structure drifts out of sync.

   `isFlagEnabled` is injected (featureFlags lives in a React context that
   this module deliberately does not import) — pass a predicate taking a
   flagKey. Rows are gated exactly as the rail gates them, so search can
   never offer a page the caller cannot open.

   noActive rows are filters over another page (Important Posts → /feed),
   not destinations, so they stay out. Deduped by key: the agent and staff
   trees overlap heavily and a searcher wants one row per place.

   🔴 RW-2581: this walk deliberately does NOT honour `railKey`. A rail gate
   takes a section off the RAIL; the routes underneath stay open, so every
   row inside one is still a live page — and dropping live pages out of
   search is precisely the dead end RW-1568 exists to prevent. A module that
   should be unreachable is turned off by its `flagKey`, which this walk does
   honour and which closes the route as well. ── */
export function navDestinations(gates = {}, isFlagEnabled = () => true) {
  const out = [];
  const seen = new Set();
  const push = (item, section, band) => {
    // Standalone sections are keyed `id`, rows are keyed `key` — both are the
    // stable handle for their kind, and using only one silently collapses
    // every standalone row into a single `undefined` dedupe slot.
    const handle = item && (item.key || item.id);
    if (!item || !handle || item.noActive || !item.path || seen.has(handle)) return;
    if (!navRoleAllows(item, gates)) return;
    if (item.flagKey && !isFlagEnabled(item.flagKey)) return;
    seen.add(handle);
    // `trail` is what the result row shows underneath the label — it answers
    // the question the searcher actually has ("where does this live?"), so
    // the next time they can walk to it without search.
    out.push({
      key: handle,
      label: item.label,
      path: item.path,
      icon: item.icon || 'compass',
      section,
      band: band || null,
      trail: [section, band, item.label].filter(Boolean).join(' › '),
      keywords: item.keywords || [], // RW-3740
    });
  };

  for (const s of [...SECTIONS, ...STAFF_SECTIONS]) {
    if (s.standalone) { push(s, s.label, null); continue; }
    for (const band of (s.bands || [{ label: null, items: s.items }])) {
      for (const item of band.items || []) push(item, s.label, band.label);
    }
  }
  for (const item of FOOTER_ITEMS) push(item, 'More', null);
  return out;
}
